import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import AIInspectionFlow from './components/AIInspectionFlow';

const API_URL = process.env.REACT_APP_API_URL || 'https://lotsense-production.up.railway.app/api';

// Get auth token from localStorage
const getAuthToken = () => localStorage.getItem('lotsense_access_token');

// Public API calls (no auth required)
const publicApi = {
  getVehicles: async () => {
    const res = await fetch(`${API_URL}/public/vehicles`);
    if (!res.ok) throw new Error('Failed to fetch vehicles');
    return res.json();
  },
  getVehicle: async (stockNumber) => {
    const res = await fetch(`${API_URL}/public/vehicles/${stockNumber}`);
    if (!res.ok) throw new Error('Vehicle not found');
    return res.json();
  },
  getVehiclePhotos: async (stockNumber) => {
    const res = await fetch(`${API_URL}/public/vehicle-photos/${stockNumber}`);
    if (!res.ok) return { photos: [] };
    return res.json();
  },
  getVehicleStatus: async (stockNumber) => {
    const res = await fetch(`${API_URL}/public/vehicle-status/${stockNumber}`);
    if (!res.ok) throw new Error('Failed to fetch status');
    return res.json();
  },
  markWorkComplete: async (stockNumber, data) => {
    const res = await fetch(`${API_URL}/public/mark-complete/${stockNumber}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to mark work complete');
    return res.json();
  },
  submitLead: async (data) => {
    const res = await fetch(`${API_URL}/public/leads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to submit');
    return res.json();
  },
  submitOffer: async (data) => {
    const res = await fetch(`${API_URL}/public/offers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to submit offer');
    return res.json();
  },
  submitVendorService: async (data) => {
    const token = getAuthToken();
    const res = await fetch(`${API_URL}/vendor-services`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to submit work log');
    return res.json();
  },
  getVendorServiceHistory: async (stockNumber) => {
    const token = getAuthToken();
    const res = await fetch(`${API_URL}/vendor-services/vehicle/${stockNumber}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) return [];
    return res.json();
  },
  // Public upload endpoints (no auth)
  getUploadInfo: async (stockNumber) => {
    const res = await fetch(`${API_URL}/public/upload-info/${stockNumber}`);
    if (!res.ok) throw new Error('Vehicle not found');
    return res.json();
  },
  uploadFiles: async (stockNumber, files, category, onProgress) => {
    const formData = new FormData();
    formData.append('category', category);
    files.forEach(file => formData.append('files', file));
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percent = Math.round((e.loaded / e.total) * 100);
          onProgress(percent);
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          try {
            const err = JSON.parse(xhr.responseText);
            reject(new Error(err.error || 'Upload failed'));
          } catch {
            reject(new Error('Upload failed'));
          }
        }
      });
      
      xhr.addEventListener('error', () => reject(new Error('Upload failed')));
      xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
      
      xhr.open('POST', `${API_URL}/public/upload/${stockNumber}`);
      xhr.send(formData);
    });
  },
  addNote: async (stockNumber, note) => {
    const res = await fetch(`${API_URL}/public/note/${stockNumber}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to add note');
    }
    return res.json();
  }
};

// Helper to strip non-numeric characters from stock number for display
const formatStockNumber = (stockNumber) => {
  return stockNumber ? stockNumber.replace(/[^0-9]/g, '') : '';
};

// Calculate out-the-door price: (price + $20 temp tag) * 1.0775 tax
const calculateOTD = (price) => {
  if (!price) return null;
  return ((parseFloat(price) + 20) * 1.0775).toFixed(2);
};

// Header component - links to /cars for public inventory, NOT main app
const Header = ({ isEmployee }) => (
  <header className="bg-neutral-900 text-white py-4 px-6 shadow-lg">
    <div className="max-w-6xl mx-auto flex items-center justify-between">
      <Link to="/cars" className="text-2xl font-bold tracking-tight">
        AMC Wholesale
      </Link>
      <nav className="flex items-center gap-4">
        <Link to="/cars" className="hover:text-blue-400 transition">Inventory</Link>
        {isEmployee && (
          <Link to="/" className="text-xs bg-neutral-700 hover:bg-neutral-600 px-3 py-1 rounded transition">
            Back to LotSense
          </Link>
        )}
        <Link to="/cars/sell" className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-medium transition">
          Sell Us Your Car
        </Link>
        <a href="mailto:chris@thewholesalecars.com" className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg font-medium transition">
          Contact Us
        </a>
      </nav>
    </div>
  </header>
);

// Footer component
const Footer = () => (
  <footer className="bg-neutral-900 text-neutral-400 py-8 px-6 mt-auto">
    <div className="max-w-6xl mx-auto text-center">
      <p className="text-sm">AMC Wholesale</p>
      <p className="text-xs mt-2">chris@thewholesalecars.com</p>
    </div>
  </footer>
);

// Photo Gallery Component
const PhotoGallery = ({ photos, fallback = true }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  if (!photos || photos.length === 0) {
    if (!fallback) return null;
    return (
      <div className="bg-gradient-to-br from-neutral-200 to-neutral-300 h-64 flex items-center justify-center">
        <div className="text-center px-4">
          <div className="text-neutral-400 text-sm">Photos update frequently</div>
          <div className="text-neutral-500 text-xs mt-1">Vehicles move fast - contact us for current photos</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="relative">
      <img 
        src={photos[currentIndex]} 
        alt="Vehicle" 
        className="w-full h-64 object-cover"
        onError={(e) => { e.target.style.display = 'none'; }}
      />
      {photos.length > 1 && (
        <>
          <button 
            onClick={() => setCurrentIndex(i => i > 0 ? i - 1 : photos.length - 1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white w-8 h-8 rounded-full hover:bg-black/70"
          >
            ‹
          </button>
          <button 
            onClick={() => setCurrentIndex(i => i < photos.length - 1 ? i + 1 : 0)}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white w-8 h-8 rounded-full hover:bg-black/70"
          >
            ›
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {photos.map((_, i) => (
              <div key={i} className={`w-2 h-2 rounded-full ${i === currentIndex ? 'bg-white' : 'bg-white/50'}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Vehicle Card for inventory list
const VehicleCard = ({ vehicle }) => (
  <Link 
    to={`/car/${vehicle.stockNumber}`}
    className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden group"
  >
    <div className="bg-gradient-to-br from-neutral-200 to-neutral-300 h-48 flex items-center justify-center">
      <div className="text-center px-4">
        <div className="text-neutral-400 text-sm">Photos update frequently</div>
        <div className="text-neutral-500 text-xs mt-1">Tap to view details</div>
      </div>
    </div>
    <div className="p-4">
      <h3 className="font-bold text-lg text-neutral-800">
        {vehicle.year} {vehicle.make} {vehicle.model}
      </h3>
      <div className="flex items-center gap-2 mt-1 text-sm text-neutral-500">
        <span>{vehicle.color || 'N/A'}</span>
        <span>-</span>
        <span>{vehicle.mileage ? `${Number(vehicle.mileage).toLocaleString()} mi` : 'N/A'}</span>
      </div>
      {vehicle.price && (
        <div className="mt-3 text-2xl font-bold text-green-600">
          ${vehicle.price.toLocaleString()}
        </div>
      )}
      <div className="mt-2 text-xs text-neutral-400">
        Stock #{formatStockNumber(vehicle.stockNumber)}
      </div>
    </div>
  </Link>
);

// Sell Your Car Form Component
const SellYourCarForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', 
    vehicleYear: '', vehicleMake: '', vehicleModel: '', vehicleMileage: '', notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await publicApi.submitLead({
        ...formData,
        stockNumber: 'SELL-REQUEST',
        message: `SELL YOUR CAR REQUEST\nVehicle: ${formData.vehicleYear} ${formData.vehicleMake} ${formData.vehicleModel}\nMileage: ${formData.vehicleMileage}\nNotes: ${formData.notes}`
      });
      setSubmitted(true);
    } catch (err) {
      alert('Failed to submit. Please email chris@thewholesalecars.com directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <h3 className="text-lg font-semibold text-green-800 mb-2">Request Received!</h3>
        <p className="text-green-700 text-sm">We'll be in touch soon about your vehicle.</p>
        <button onClick={onClose} className="mt-4 text-blue-600 hover:underline text-sm">Close</button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-neutral-800">Sell Your Car</h3>
          <p className="text-sm text-neutral-500">Top dollar paid. No obligation to buy from us.</p>
        </div>
        <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl">&times;</button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            placeholder="Your Name *"
            required
            value={formData.name}
            onChange={e => setFormData({...formData, name: e.target.value})}
            className="px-3 py-2 border border-neutral-300 rounded text-sm"
          />
          <input
            type="tel"
            placeholder="Phone *"
            required
            value={formData.phone}
            onChange={e => setFormData({...formData, phone: e.target.value})}
            className="px-3 py-2 border border-neutral-300 rounded text-sm"
          />
        </div>
        <input
          type="email"
          placeholder="Email"
          value={formData.email}
          onChange={e => setFormData({...formData, email: e.target.value})}
          className="w-full px-3 py-2 border border-neutral-300 rounded text-sm"
        />
        <div className="border-t pt-3 mt-3">
          <p className="text-xs text-neutral-500 mb-2">Your Vehicle Info:</p>
          <div className="grid grid-cols-3 gap-2">
            <input
              type="text"
              placeholder="Year *"
              required
              value={formData.vehicleYear}
              onChange={e => setFormData({...formData, vehicleYear: e.target.value})}
              className="px-3 py-2 border border-neutral-300 rounded text-sm"
            />
            <input
              type="text"
              placeholder="Make *"
              required
              value={formData.vehicleMake}
              onChange={e => setFormData({...formData, vehicleMake: e.target.value})}
              className="px-3 py-2 border border-neutral-300 rounded text-sm"
            />
            <input
              type="text"
              placeholder="Model *"
              required
              value={formData.vehicleModel}
              onChange={e => setFormData({...formData, vehicleModel: e.target.value})}
              className="px-3 py-2 border border-neutral-300 rounded text-sm"
            />
          </div>
          <input
            type="text"
            placeholder="Mileage"
            value={formData.vehicleMileage}
            onChange={e => setFormData({...formData, vehicleMileage: e.target.value})}
            className="w-full mt-2 px-3 py-2 border border-neutral-300 rounded text-sm"
          />
          <textarea
            placeholder="Any notes about the vehicle?"
            value={formData.notes}
            onChange={e => setFormData({...formData, notes: e.target.value})}
            className="w-full mt-2 px-3 py-2 border border-neutral-300 rounded text-sm"
            rows={2}
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded transition disabled:opacity-50"
        >
          {submitting ? 'Submitting...' : 'Submit - Get Your Offer'}
        </button>
      </form>
    </div>
  );
};

// Standalone Sell Your Car Page
const SellYourCarPage = () => {
  const navigate = useNavigate();
  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <button 
        onClick={() => navigate('/cars')} 
        className="text-blue-600 hover:underline mb-6 flex items-center gap-1"
      >
        &larr; Back to Inventory
      </button>
      <SellYourCarForm onClose={() => navigate('/cars')} />
    </div>
  );
};

// Inventory Page - Browse all vehicles
const InventoryPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSellForm, setShowSellForm] = useState(false);
  const [filters, setFilters] = useState({
    make: '',
    yearMin: '',
    yearMax: '',
    mileageMax: '',
    priceMax: ''
  });

  useEffect(() => {
    publicApi.getVehicles()
      .then(setVehicles)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Get unique makes for dropdown
  const uniqueMakes = [...new Set(vehicles.map(v => v.make).filter(Boolean))].sort();

  const filtered = vehicles.filter(v => {
    // Text search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = (
        (v.year?.toString() || '').includes(search) ||
        (v.make || '').toLowerCase().includes(search) ||
        (v.model || '').toLowerCase().includes(search) ||
        (v.color || '').toLowerCase().includes(search) ||
        (v.stockNumber || '').toLowerCase().includes(search)
      );
      if (!matchesSearch) return false;
    }
    
    // Make filter
    if (filters.make && v.make !== filters.make) return false;
    
    // Year range
    if (filters.yearMin && v.year < parseInt(filters.yearMin)) return false;
    if (filters.yearMax && v.year > parseInt(filters.yearMax)) return false;
    
    // Mileage max
    if (filters.mileageMax && v.mileage > parseInt(filters.mileageMax)) return false;
    
    // Price max
    if (filters.priceMax && v.price > parseInt(filters.priceMax)) return false;
    
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-neutral-500">Loading inventory...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* About Section */}
      <div className="bg-neutral-100 rounded-lg p-6 mb-8">
        <h2 className="text-xl font-bold text-neutral-800 mb-4">How We Work</h2>
        <div className="grid md:grid-cols-2 gap-6 text-sm text-neutral-700">
          <div>
            <h3 className="font-semibold mb-1">What We Do</h3>
            <p>We're a wholesale car operation. We buy vehicles at auction, through dealer trades, and off the street. We fix what needs fixing and sell at wholesale prices to dealers and the public.</p>
          </div>
          <div>
            <h3 className="font-semibold mb-1">What That Means For You</h3>
            <p>You're buying at dealer pricing, not retail markup. Cash or bank check only. No financing, no payments. You save money because we skip the retail song and dance.</p>
          </div>
          <div>
            <h3 className="font-semibold mb-1">What To Expect</h3>
            <p>Most cars have been through our shop. Some have dings, scratches, or wear. Some may still need mechanical work. That's why they're priced the way they are. If you want a perfect car with a bow on it, go to a big dealer and pay thousands more.</p>
          </div>
          <div>
            <h3 className="font-semibold mb-1">How It Works</h3>
            <p>Browse what we have. Come look at it, drive it. Bring cash or a bank check. Drive it home. No hidden fees. No BS. If a price seems too high, feel free to make an offer.</p>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-neutral-200 flex flex-wrap gap-4 items-center justify-between">
          <p className="text-sm text-neutral-600">If this sounds like your kind of thing, hit Contact Us and we'll go from there.</p>
          <button
            onClick={() => setShowSellForm(true)}
            className="bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2 rounded transition text-sm"
          >
            Interested in Selling Your Car?
          </button>
        </div>
      </div>

      {/* Sell Your Car Form */}
      {showSellForm && (
        <div className="mb-8">
          <SellYourCarForm onClose={() => setShowSellForm(false)} />
        </div>
      )}

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-neutral-800 mb-2">Current Inventory</h1>
        <p className="text-neutral-500">Vehicles move fast - contact us to confirm availability</p>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <input
          type="text"
          placeholder="Search by year, make, model, color..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full max-w-md px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        
        {/* Filters Row */}
        <div className="flex flex-wrap gap-3">
          <select
            value={filters.make}
            onChange={e => setFilters({...filters, make: e.target.value})}
            className="px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Makes</option>
            {uniqueMakes.map(make => (
              <option key={make} value={make}>{make}</option>
            ))}
          </select>
          
          <div className="flex items-center gap-1">
            <input
              type="number"
              placeholder="Year Min"
              value={filters.yearMin}
              onChange={e => setFilters({...filters, yearMin: e.target.value})}
              className="w-24 px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-neutral-400">-</span>
            <input
              type="number"
              placeholder="Year Max"
              value={filters.yearMax}
              onChange={e => setFilters({...filters, yearMax: e.target.value})}
              className="w-24 px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <input
            type="number"
            placeholder="Max Mileage"
            value={filters.mileageMax}
            onChange={e => setFilters({...filters, mileageMax: e.target.value})}
            className="w-32 px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          
          <input
            type="number"
            placeholder="Max Price"
            value={filters.priceMax}
            onChange={e => setFilters({...filters, priceMax: e.target.value})}
            className="w-32 px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          
          {(filters.make || filters.yearMin || filters.yearMax || filters.mileageMax || filters.priceMax) && (
            <button
              onClick={() => setFilters({ make: '', yearMin: '', yearMax: '', mileageMax: '', priceMax: '' })}
              className="px-3 py-2 text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-neutral-500">
          No vehicles found matching your search.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(vehicle => (
            <VehicleCard key={vehicle.id} vehicle={vehicle} />
          ))}
        </div>
      )}

      <div className="mt-8 text-center text-sm text-neutral-400">
        Showing {filtered.length} of {vehicles.length} vehicles
      </div>
    </div>
  );
};

// Status Checklist Item
const ChecklistItem = ({ label, checked }) => (
  <div className="flex items-center gap-2 py-1">
    <span className={`text-lg ${checked ? 'text-green-600' : 'text-neutral-300'}`}>
      {checked ? '✓' : '○'}
    </span>
    <span className={checked ? 'text-neutral-800' : 'text-neutral-400'}>{label}</span>
  </div>
);

// CUSTOMER VIEW - Public visitor (not logged in)
const CustomerView = ({ vehicle, photos }) => {
  const [activeTab, setActiveTab] = useState('inquiry'); // 'inquiry' or 'offer'
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', message: '', offerAmount: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmitInquiry = async (e) => {
    e.preventDefault();
    if (!formData.name || (!formData.phone && !formData.email)) {
      alert('Please provide your name and at least a phone number or email.');
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.submitLead({
        vehicleId: vehicle?.id,
        stockNumber: vehicle?.stockNumber,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        message: formData.message
      });
      setSubmitted(true);
    } catch (err) {
      alert('Failed to submit. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitOffer = async (e) => {
    e.preventDefault();
    if (!formData.name || (!formData.phone && !formData.email)) {
      alert('Please provide your name and at least a phone number or email.');
      return;
    }
    if (!formData.offerAmount) {
      alert('Please enter your offer amount.');
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.submitOffer({
        vehicleId: vehicle?.id,
        stockNumber: vehicle?.stockNumber,
        customerName: formData.name,
        phone: formData.phone,
        email: formData.email,
        offerAmount: parseFloat(formData.offerAmount),
        message: formData.message
      });
      setSubmitted(true);
    } catch (err) {
      alert('Failed to submit offer. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Vehicle Info */}
      <div>
        <div className="rounded-xl overflow-hidden mb-6">
          <PhotoGallery photos={photos} />
        </div>

        <h1 className="text-3xl font-bold text-neutral-800 mb-2">
          {vehicle.year} {vehicle.make} {vehicle.model}
        </h1>

        <div className="space-y-3 mt-6">
          <div className="flex justify-between py-2 border-b border-neutral-200">
            <span className="text-neutral-500">Stock #</span>
            <span className="font-mono font-medium">{vehicle.stockNumber}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-neutral-200">
            <span className="text-neutral-500">VIN</span>
            <span className="font-mono text-sm">{vehicle.vin}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-neutral-200">
            <span className="text-neutral-500">Color</span>
            <span className="font-medium">{vehicle.color || 'N/A'}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-neutral-200">
            <span className="text-neutral-500">Mileage</span>
            <span className="font-medium">{vehicle.mileage ? `${Number(vehicle.mileage).toLocaleString()} miles` : 'N/A'}</span>
          </div>
          {vehicle.price && (
            <div className="flex justify-between py-3 bg-green-50 rounded-lg px-4 mt-4">
              <span className="text-green-700 font-medium">Price</span>
              <span className="text-2xl font-bold text-green-600">${vehicle.price.toLocaleString()}</span>
            </div>
          )}
        </div>

        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-amber-800 text-sm">
            <strong>Note:</strong> All vehicles sold as-is. Message us for details on any known issues or to schedule a viewing.
          </p>
        </div>
      </div>

      {/* Forms */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        {submitted ? (
          <div className="text-center py-8">
            <div className="text-5xl mb-4">✓</div>
            <h2 className="text-2xl font-bold text-green-600 mb-2">Thank You!</h2>
            <p className="text-neutral-600">We've received your {activeTab === 'offer' ? 'offer' : 'inquiry'} and will contact you soon.</p>
            <Link to="/cars" className="mt-6 inline-block text-blue-600 hover:underline">
              Browse more vehicles
            </Link>
          </div>
        ) : (
          <>
            {/* Tab Selector */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setActiveTab('inquiry')}
                className={`flex-1 py-2 rounded-lg font-medium transition ${activeTab === 'inquiry' ? 'bg-blue-600 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
              >
                Send Inquiry
              </button>
              <button
                onClick={() => setActiveTab('offer')}
                className={`flex-1 py-2 rounded-lg font-medium transition ${activeTab === 'offer' ? 'bg-green-600 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
              >
                Make an Offer
              </button>
            </div>

            <form onSubmit={activeTab === 'offer' ? handleSubmitOffer : handleSubmitInquiry} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="you@example.com"
                />
              </div>

              {activeTab === 'offer' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Your Offer Amount *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">$</span>
                    <input
                      type="number"
                      value={formData.offerAmount}
                      onChange={e => setFormData({ ...formData, offerAmount: e.target.value })}
                      className="w-full pl-8 pr-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="0.00"
                      required={activeTab === 'offer'}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Message</label>
                <textarea
                  value={formData.message}
                  onChange={e => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none"
                  placeholder={activeTab === 'offer' ? "Any additional details about your offer..." : "I'm interested in this vehicle..."}
                />
              </div>

              <p className="text-xs text-neutral-400">* Phone or email required</p>

              <button
                type="submit"
                disabled={submitting}
                className={`w-full font-medium py-3 rounded-lg transition disabled:opacity-50 ${activeTab === 'offer' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
              >
                {submitting ? 'Sending...' : (activeTab === 'offer' ? 'Submit Offer' : 'Send Inquiry')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// EMPLOYEE VIEW - Logged in employee (not vendor)
const EmployeeView = ({ vehicle, photos, statusData }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Vehicle Info */}
      <div>
        <div className="rounded-xl overflow-hidden mb-6">
          <PhotoGallery photos={photos} />
        </div>

        <h1 className="text-3xl font-bold text-neutral-800 mb-2">
          {vehicle.year} {vehicle.make} {vehicle.model}
        </h1>
        <p className="text-neutral-500 font-mono">Stock #{vehicle.stockNumber}</p>

        {/* Note: estimatedSale only shown to employees, not public customers */}
        {statusData?.estimatedSale && (
          <div className="mt-4 p-4 bg-green-50 rounded-lg">
            <span className="text-green-700 font-medium">Est. Sale Price: </span>
            <span className="text-2xl font-bold text-green-600">${parseFloat(statusData.estimatedSale).toLocaleString()}</span>
          </div>
        )}

        <div className="mt-6">
          <Link 
            to="/" 
            className="inline-block bg-neutral-800 hover:bg-neutral-700 text-white px-4 py-2 rounded-lg transition"
          >
            Open in LotSense
          </Link>
        </div>
      </div>

      {/* Status Checklist */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-neutral-800 mb-4">Status Checklist</h2>
        
        {statusData?.checklist ? (
          <div className="space-y-2">
            <ChecklistItem label="Title Received" checked={statusData.checklist.titleReceived} />
            <ChecklistItem label="RO Created" checked={statusData.checklist.roCreated} />
            <ChecklistItem label="Inspected" checked={statusData.checklist.inspected} />
            <ChecklistItem label="Needs Detail" checked={statusData.checklist.needsDetail} />
            <ChecklistItem label="Detail Completed" checked={statusData.checklist.detailCompleted} />
            <ChecklistItem label="Parts Ordered" checked={statusData.checklist.partsOrdered} />
            <ChecklistItem label="Parts Received" checked={statusData.checklist.partsReceived} />
          </div>
        ) : (
          <p className="text-neutral-500">Loading status...</p>
        )}

        {/* Completed Work */}
        {statusData?.completedWork?.length > 0 && (
          <div className="mt-6">
            <h3 className="font-semibold text-neutral-700 mb-2">Completed Work</h3>
            <ul className="space-y-1 text-sm">
              {statusData.completedWork.map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-neutral-600">
                  <span className="text-green-600">✓</span>
                  {item.description}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pending Vendor Work */}
        {statusData?.pendingVendorWork?.length > 0 && (
          <div className="mt-6">
            <h3 className="font-semibold text-neutral-700 mb-2">Pending Vendor Work</h3>
            <ul className="space-y-1 text-sm">
              {statusData.pendingVendorWork.map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-amber-600">
                  <span>⏳</span>
                  {item.vendor_name}: {item.service_type}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

// VENDOR VIEW - Logged in as vendor role
const VendorView = ({ vehicle, photos, stockNumber }) => {
  const [formData, setFormData] = useState({ serviceType: '', description: '', amount: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    publicApi.getVendorServiceHistory(stockNumber).then(setHistory).catch(console.error);
  }, [stockNumber]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.serviceType || !formData.description) {
      alert('Please select a service type and provide a description.');
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.submitVendorService({
        stockNumber,
        serviceType: formData.serviceType,
        description: formData.description,
        amount: parseFloat(formData.amount) || 0
      });
      setSubmitted(true);
      // Refresh history
      publicApi.getVendorServiceHistory(stockNumber).then(setHistory).catch(console.error);
    } catch (err) {
      alert('Failed to submit work log: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const SERVICE_TYPES = [
    { value: 'detail', label: 'Detail' },
    { value: 'mechanical', label: 'Mechanical' },
    { value: 'body_work', label: 'Body Work' },
    { value: 'paint', label: 'Paint' },
    { value: 'glass', label: 'Glass' },
    { value: 'pdr', label: 'PDR (Paintless Dent Repair)' },
    { value: 'tint', label: 'Tint' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Vehicle Info */}
      <div>
        <div className="rounded-xl overflow-hidden mb-6">
          <PhotoGallery photos={photos} />
        </div>

        <h1 className="text-3xl font-bold text-neutral-800 mb-2">
          {vehicle.year} {vehicle.make} {vehicle.model}
        </h1>
        <p className="text-neutral-500 font-mono">Stock #{vehicle.stockNumber}</p>

        {/* Previous Submissions */}
        {history.length > 0 && (
          <div className="mt-6 bg-neutral-50 rounded-lg p-4">
            <h3 className="font-semibold text-neutral-700 mb-3">Your Previous Submissions</h3>
            <div className="space-y-2">
              {history.map((item, i) => (
                <div key={i} className="bg-white rounded p-3 border border-neutral-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="font-medium">{item.service_type}</span>
                      <p className="text-sm text-neutral-500">{item.description}</p>
                    </div>
                    <div className="text-right">
                      <span className="font-mono">${parseFloat(item.amount || 0).toLocaleString()}</span>
                      <div className={`text-xs mt-1 px-2 py-0.5 rounded ${
                        item.status === 'approved' ? 'bg-green-100 text-green-700' :
                        item.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {item.status === 'pending_review' ? 'Pending Review' : item.status}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Work Log Form */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-neutral-800 mb-4">Log Work Performed</h2>
        
        {submitted ? (
          <div className="text-center py-8">
            <div className="text-5xl mb-4">✓</div>
            <h2 className="text-2xl font-bold text-green-600 mb-2">Work Log Submitted!</h2>
            <p className="text-neutral-600">Admin has been notified and will review your submission.</p>
            <button 
              onClick={() => { setSubmitted(false); setFormData({ serviceType: '', description: '', amount: '' }); }}
              className="mt-6 text-blue-600 hover:underline"
            >
              Submit Another
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Service Type *</label>
              <select
                value={formData.serviceType}
                onChange={e => setFormData({ ...formData, serviceType: e.target.value })}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select service type...</option>
                {SERVICE_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Description of Work *</label>
              <textarea
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-32 resize-none"
                placeholder="Describe the work you performed..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Your Charge Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">$</span>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={e => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full pl-8 pr-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 rounded-lg transition disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Work Log'}
            </button>

            <p className="text-xs text-neutral-400 text-center">
              Admin will be notified and will review your submission.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

// Upload View Component - NO LOGIN REQUIRED (but requires employee passcode)
const UploadView = ({ vehicle, stockNumber, onBack }) => {
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [noteSuccess, setNoteSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Passcode gate - hash of "4015" (simple obfuscation, not security)
  const [isVerified, setIsVerified] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [passcodeError, setPasscodeError] = useState(false);
  
  // Simple hash function to avoid plain text passcode in frontend
  const hashCode = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash;
  };
  
  const CORRECT_HASH = 1596832; // hash of "4015"
  
  const handlePasscodeSubmit = (e) => {
    e.preventDefault();
    if (hashCode(passcode) === CORRECT_HASH) {
      setIsVerified(true);
      setPasscodeError(false);
    } else {
      setPasscodeError(true);
      setPasscode('');
    }
  };

  const handleUpload = async (category, acceptTypes, useCamera = false) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = acceptTypes;
    input.multiple = category === 'photos' && !useCamera;
    if (useCamera) {
      input.capture = 'environment';
    }
    
    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      
      setUploading(true);
      setUploadProgress(0);
      setError(null);
      setSuccess(null);
      
      try {
        const result = await publicApi.uploadFiles(stockNumber, files, category, (progress) => {
          setUploadProgress(progress);
        });
        setSuccess(`${result.files.length} file(s) uploaded successfully!`);
        setTimeout(() => setSuccess(null), 5000);
      } catch (err) {
        setError(err.message);
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    };
    
    input.click();
  };

  const handleNoteSubmit = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    
    setNoteSubmitting(true);
    setError(null);
    
    try {
      await publicApi.addNote(stockNumber, note);
      setNote('');
      setNoteSuccess(true);
      setTimeout(() => setNoteSuccess(false), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setNoteSubmitting(false);
    }
  };

  // Passcode entry screen
  if (!isVerified) {
    return (
      <div className="max-w-md mx-auto py-8 px-4">
        <button 
          onClick={onBack}
          className="text-blue-600 hover:underline mb-6 flex items-center gap-1"
        >
          &larr; Back
        </button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-neutral-800 mb-2">Employee Access</h1>
          <p className="text-neutral-500 text-sm">Enter your 4-digit code to upload files</p>
        </div>

        <form onSubmit={handlePasscodeSubmit} className="space-y-4">
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            value={passcode}
            onChange={(e) => {
              setPasscode(e.target.value.replace(/\D/g, ''));
              setPasscodeError(false);
            }}
            placeholder="••••"
            className={`w-full text-center text-2xl tracking-widest py-4 border rounded-lg focus:outline-none focus:ring-2 ${
              passcodeError 
                ? 'border-red-300 focus:ring-red-500 bg-red-50' 
                : 'border-neutral-300 focus:ring-blue-500'
            }`}
            autoFocus
          />
          
          {passcodeError && (
            <p className="text-red-600 text-sm text-center">Incorrect code. Please try again.</p>
          )}
          
          <button
            type="submit"
            disabled={passcode.length !== 4}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-neutral-300 text-white font-medium py-3 rounded-lg transition"
          >
            Unlock Uploads
          </button>
        </form>

        <div className="text-center mt-8">
          <p className="text-xs text-neutral-400">
            {vehicle.year} {vehicle.make} {vehicle.model}
          </p>
          <p className="text-xs text-neutral-400 font-mono">Stock #{formatStockNumber(vehicle.stockNumber)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-8 px-4">
      <button 
        onClick={onBack}
        className="text-blue-600 hover:underline mb-6 flex items-center gap-1"
      >
        &larr; Back
      </button>

      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-neutral-800 mb-2">
          {vehicle.year} {vehicle.make} {vehicle.model}
        </h1>
        <p className="text-neutral-500 font-mono">Stock #{formatStockNumber(vehicle.stockNumber)}</p>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          ✓ {success}
        </div>
      )}

      {/* Upload Buttons */}
      <div className="space-y-4 mb-8">
        <h2 className="text-lg font-semibold text-neutral-700 mb-4">Upload Files</h2>
        
        {/* BlueDriver Scan */}
        <button
          onClick={() => handleUpload('bluedriver', '.pdf,image/*')}
          disabled={uploading}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-4 px-6 rounded-lg transition flex items-center justify-center gap-3"
        >
          <span className="text-xl">📄</span>
          {uploading ? `Uploading... ${uploadProgress}%` : 'Upload BlueDriver Scan'}
        </button>
        
        {/* Photos Section */}
        <div className="bg-green-50 rounded-lg p-3">
          <div className="text-sm font-medium text-green-800 mb-2">📸 Photos</div>
          <div className="flex gap-2">
            <button
              onClick={() => handleUpload('photos', 'image/*', true)}
              disabled={uploading}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium py-3 px-4 rounded-lg transition flex items-center justify-center gap-2"
            >
              <span>📷</span>
              {uploading ? `${uploadProgress}%` : 'Take Photo'}
            </button>
            <button
              onClick={() => handleUpload('photos', 'image/*', false)}
              disabled={uploading}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium py-3 px-4 rounded-lg transition flex items-center justify-center gap-2"
            >
              <span>🖼️</span>
              {uploading ? `${uploadProgress}%` : 'From Library'}
            </button>
          </div>
        </div>
        
        {/* Videos Section */}
        <div className="bg-purple-50 rounded-lg p-3">
          <div className="text-sm font-medium text-purple-800 mb-2">🎬 Videos</div>
          <div className="flex gap-2">
            <button
              onClick={() => handleUpload('video', 'video/*', true)}
              disabled={uploading}
              className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white font-medium py-3 px-4 rounded-lg transition flex items-center justify-center gap-2"
            >
              <span>🎥</span>
              {uploading ? `${uploadProgress}%` : 'Record Video'}
            </button>
            <button
              onClick={() => handleUpload('video', 'video/*', false)}
              disabled={uploading}
              className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white font-medium py-3 px-4 rounded-lg transition flex items-center justify-center gap-2"
            >
              <span>📁</span>
              {uploading ? `${uploadProgress}%` : 'From Library'}
            </button>
          </div>
        </div>
      </div>

      {/* Add Note Section */}
      <div className="border-t border-neutral-200 pt-6">
        <h2 className="text-lg font-semibold text-neutral-700 mb-4">Add a Note</h2>
        
        {noteSuccess && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
            ✓ Note added successfully!
          </div>
        )}
        
        <form onSubmit={handleNoteSubmit}>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Type your note here..."
            className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-28 resize-none mb-3"
            maxLength={2000}
          />
          <button
            type="submit"
            disabled={noteSubmitting || !note.trim()}
            className="w-full bg-neutral-800 hover:bg-neutral-700 disabled:bg-neutral-400 text-white font-medium py-3 rounded-lg transition"
          >
            {noteSubmitting ? 'Submitting...' : 'Submit Note'}
          </button>
        </form>
      </div>

      <p className="text-xs text-neutral-400 text-center mt-6">
        Files are uploaded directly to this vehicle's record.
      </p>
    </div>
  );
};

// Mark Complete View Component - NO LOGIN REQUIRED (but requires employee passcode)
const MarkCompleteView = ({ vehicle, stockNumber, onBack }) => {
  const [isVerified, setIsVerified] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [passcodeError, setPasscodeError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [pendingWork, setPendingWork] = useState([]);
  const [completedItems, setCompletedItems] = useState([]);
  
  // Simple hash function to avoid plain text passcode in frontend
  const hashCode = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash;
  };
  
  const CORRECT_HASH = 1596832; // hash of "4015"
  
  const handlePasscodeSubmit = (e) => {
    e.preventDefault();
    if (hashCode(passcode) === CORRECT_HASH) {
      setIsVerified(true);
      setPasscodeError(false);
      loadVehicleData();
    } else {
      setPasscodeError(true);
      setPasscode('');
    }
  };
  
  const loadVehicleData = async () => {
    setLoading(true);
    try {
      const statusData = await publicApi.getVehicleStatus(stockNumber);
      setPendingWork(statusData?.pendingVendorWork || []);
      setNewStatus(vehicle.reconWorkflowStatus || 'awaiting_inspection');
    } catch (err) {
      console.error('Failed to load vehicle data:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const toggleWorkItem = (itemId) => {
    setCompletedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation: must have at least one item checked OR status change
    if (completedItems.length === 0 && newStatus === vehicle.reconWorkflowStatus) {
      setError('Please select at least one work item to mark complete or change the status');
      return;
    }
    
    if (!notes.trim()) {
      setError('Please add a note describing the work completed');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      await publicApi.markWorkComplete(stockNumber, {
        completedItems,
        newStatus,
        notes: notes.trim()
      });
      setSuccess('Work marked as complete!');
      setCompletedItems([]);
      setNotes('');
      // Reload data
      loadVehicleData();
    } catch (err) {
      setError(err.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Workflow statuses for dropdown
  const WORKFLOW_STATUSES = [
    { value: 'awaiting_inspection', label: 'Awaiting Inspection' },
    { value: 'parts_ordered', label: 'Parts Ordered' },
    { value: 'repairs_in_progress', label: 'Repairs In Progress' },
    { value: 'needs_outside_vendor', label: 'Needs Outside Vendor' },
    { value: 'needs_inhouse_body', label: 'Needs In-House Body' },
    { value: 'needs_outsource_body', label: 'Needs Outsource Body' },
    { value: 'needs_outside_repair', label: 'Needs Outside Repair' },
    { value: 'at_detail', label: 'At Detail' },
    { value: 'ready_for_sale', label: 'Ready for Sale' }
  ];

  // Passcode entry screen
  if (!isVerified) {
    return (
      <div className="max-w-md mx-auto py-8 px-4">
        <button 
          onClick={onBack}
          className="text-blue-600 hover:underline mb-6 flex items-center gap-1"
        >
          &larr; Back
        </button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-neutral-800 mb-2">Mark Work Complete</h1>
          <p className="text-neutral-500 text-sm">
            {vehicle.year} {vehicle.make} {vehicle.model}
          </p>
          <p className="text-neutral-400 font-mono text-sm">Stock #{stockNumber}</p>
        </div>

        <form onSubmit={handlePasscodeSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Enter Employee Passcode
            </label>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={passcode}
              onChange={(e) => setPasscode(e.target.value.replace(/\D/g, ''))}
              placeholder="4-digit code"
              className={`w-full px-4 py-3 text-center text-2xl tracking-widest border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                passcodeError ? 'border-red-500 bg-red-50' : 'border-neutral-300'
              }`}
              autoFocus
            />
            {passcodeError && (
              <p className="text-red-600 text-sm mt-2 text-center">Incorrect passcode. Try again.</p>
            )}
          </div>
          <button
            type="submit"
            disabled={passcode.length !== 4}
            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-neutral-300 text-white font-medium py-3 rounded-lg transition"
          >
            Continue
          </button>
        </form>
      </div>
    );
  }

  // Main mark complete interface
  return (
    <div className="max-w-md mx-auto py-8 px-4">
      <button 
        onClick={onBack}
        className="text-blue-600 hover:underline mb-6 flex items-center gap-1"
      >
        &larr; Back
      </button>

      <div className="text-center mb-6">
        <h1 className="text-xl font-bold text-neutral-800 mb-1">Mark Work Complete</h1>
        <p className="text-neutral-500 text-sm">
          {vehicle.year} {vehicle.make} {vehicle.model} • #{stockNumber}
        </p>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          ✓ {success}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-neutral-500">Loading...</div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Current Status */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Update Status
            </label>
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {WORKFLOW_STATUSES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* Pending Vendor Work */}
          {pendingWork.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Mark Vendor Work Complete
              </label>
              <div className="space-y-2 bg-neutral-50 rounded-lg p-3">
                {pendingWork.map((item, idx) => (
                  <label 
                    key={item.id || idx}
                    className="flex items-center gap-3 p-2 bg-white rounded border cursor-pointer hover:bg-emerald-50"
                  >
                    <input
                      type="checkbox"
                      checked={completedItems.includes(item.id || idx)}
                      onChange={() => toggleWorkItem(item.id || idx)}
                      className="w-5 h-5 rounded text-emerald-600"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{item.vendor_name || item.vendorName}</div>
                      <div className="text-xs text-neutral-500">{item.service_type || item.serviceType}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {pendingWork.length === 0 && (
            <div className="bg-neutral-50 rounded-lg p-4 text-center text-neutral-500 text-sm">
              No pending vendor work found for this vehicle.
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Notes <span className="text-red-500">*</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Describe the work completed..."
              className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 h-24 resize-none"
              required
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-neutral-400 text-white font-medium py-3 rounded-lg transition"
          >
            {submitting ? 'Submitting...' : 'Mark Complete'}
          </button>
        </form>
      )}
    </div>
  );
};

// Role Selection Component - shown when scanning QR code
const RoleSelector = ({ vehicle, onSelectRole, onSellCar, onUpload, onBeginInspection }) => {
  return (
    <div className="max-w-md mx-auto text-center py-12">
      <h1 className="text-2xl font-bold text-neutral-800 mb-2">
        {vehicle.year} {vehicle.make} {vehicle.model}
      </h1>
      <p className="text-neutral-500 font-mono mb-8">Stock #{formatStockNumber(vehicle.stockNumber)}</p>
      
      <h2 className="text-lg font-semibold text-neutral-700 mb-6">How can we help you today?</h2>
      
      <div className="space-y-3">
        <button
          onClick={() => onSelectRole('customer')}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-4 px-6 rounded-lg transition"
        >
          I'm interested in this vehicle
        </button>
        
        <button
          onClick={onSellCar}
          className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-4 px-6 rounded-lg transition"
        >
          Sell Us Your Car
        </button>
        
        <button
          onClick={() => onSelectRole('employee')}
          className="w-full bg-neutral-700 hover:bg-neutral-600 text-white font-medium py-4 px-6 rounded-lg transition"
        >
          Employee - Check Status
        </button>
        
        <button
          onClick={() => onSelectRole('vendor')}
          className="w-full bg-amber-600 hover:bg-amber-700 text-white font-medium py-4 px-6 rounded-lg transition"
        >
          Vendor - Log Work
        </button>
        
        <button
          onClick={onUpload}
          className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-medium py-4 px-6 rounded-lg transition flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          Upload Files / Add Note
        </button>
        
        <button
          onClick={() => onSelectRole('markComplete')}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-4 px-6 rounded-lg transition flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Mark Work Complete
        </button>
        
        <button
          onClick={onBeginInspection}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-4 px-6 rounded-lg transition flex items-center justify-center gap-2"
        >
          <span className="text-xl">🔍</span>
          Begin Inspection
        </button>
      </div>
    </div>
  );
};

// Customer Disclaimer Screen - shown before vehicle details
const CustomerDisclaimer = ({ vehicle, onContinue }) => {
  const [showOTD, setShowOTD] = useState(false);
  const otdPrice = calculateOTD(vehicle.price);
  
  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <h1 className="text-2xl font-bold text-neutral-800 mb-2">
        {vehicle.year} {vehicle.make} {vehicle.model}
      </h1>
      <p className="text-neutral-500 font-mono mb-6">Stock #{formatStockNumber(vehicle.stockNumber)}</p>
      
      <div className="bg-neutral-100 rounded-lg p-6 mb-6">
        <h2 className="text-lg font-bold text-neutral-800 mb-4">Before You Continue - Please Read</h2>
        
        <div className="space-y-4 text-neutral-700 text-sm">
          <div>
            <h3 className="font-semibold mb-1">What We Do</h3>
            <p>We're a wholesale car operation. We buy vehicles at auction, through dealer trades, and off the street. We fix what needs fixing and sell at wholesale prices to dealers and the public.</p>
          </div>
          
          <div>
            <h3 className="font-semibold mb-1">What That Means For You</h3>
            <p>You're buying at dealer pricing, not retail markup. Cash or bank check only. No financing, no payments. You save money because we skip the retail song and dance.</p>
          </div>
          
          <div>
            <h3 className="font-semibold mb-1">What To Expect</h3>
            <p>Most cars have been through our shop. Some have dings, scratches, or wear. Some may still need mechanical work. That's why they're priced the way they are.</p>
            <p className="mt-2">If you want a perfect car with a bow on it, go to a big dealer and pay thousands more. If you want a solid vehicle at a real price and you're not afraid of a little character, come see us. If a price seems too high, feel free to make an offer.</p>
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded p-3">
            <h3 className="font-semibold text-amber-800 mb-1">Important Notice</h3>
            <p className="text-amber-900">All vehicles are sold completely AS-IS with NO WARRANTY. All sales are final. By continuing, you acknowledge and accept these terms.</p>
          </div>
        </div>
      </div>
      
      {/* Pricing Section */}
      <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-neutral-800 mb-3">Pricing</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>Vehicle Price:</span>
            <span className="font-semibold">${vehicle.price?.toLocaleString() || 'Call for price'}</span>
          </div>
          <div className="flex justify-between text-neutral-500">
            <span>+ Temp Tag:</span>
            <span>$20</span>
          </div>
          <div className="flex justify-between text-neutral-500">
            <span>+ Lucas County Tax (7.75%):</span>
            <span>Applied to total</span>
          </div>
          <p className="text-xs text-neutral-400 mt-2">Price includes everything except tax and temp tag. No hidden fees.</p>
          <hr className="my-2" />
          {showOTD && otdPrice ? (
            <div className="flex justify-between text-lg font-bold text-green-700">
              <span>Out-the-Door Total:</span>
              <span>${parseFloat(otdPrice).toLocaleString()}</span>
            </div>
          ) : (
            <button
              onClick={() => setShowOTD(true)}
              className="w-full bg-neutral-200 hover:bg-neutral-300 text-neutral-700 font-medium py-2 px-4 rounded transition text-sm"
            >
              Calculate Out-the-Door Price
            </button>
          )}
        </div>
      </div>
      
      <button
        onClick={onContinue}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-4 px-6 rounded-lg transition"
      >
        I Understand - Show Me The Vehicle
      </button>
    </div>
  );
};

// Single Vehicle Page - Role-based views
const VehiclePage = () => {
  const { stockNumber } = useParams();
  const navigate = useNavigate();
  
  // Try to get auth context - may fail if not wrapped in AuthProvider
  let currentUser = null;
  try {
    const auth = useAuth();
    currentUser = auth?.currentUser;
  } catch (e) {
    // Not in auth context, treat as public
  }
  
  const [vehicle, setVehicle] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null); // null = show selector
  const [showDisclaimer, setShowDisclaimer] = useState(false); // customer disclaimer screen
  const [showSellForm, setShowSellForm] = useState(false); // sell your car form
  const [showUpload, setShowUpload] = useState(false); // upload files view (no login required)
  const [showAIInspection, setShowAIInspection] = useState(false); // AI-powered inspection flow

  // If logged in, auto-select role based on user type
  const isLoggedInVendor = currentUser?.role === 'vendor';
  const isLoggedInEmployee = currentUser && !isLoggedInVendor;

  // Auto-select role if logged in
  useEffect(() => {
    if (isLoggedInVendor) {
      setSelectedRole('vendor');
    } else if (isLoggedInEmployee) {
      setSelectedRole('employee');
    }
  }, [isLoggedInVendor, isLoggedInEmployee]);

  // Load vehicle data
  useEffect(() => {
    const loadData = async () => {
      try {
        const vehicleData = await publicApi.getVehicle(stockNumber);
        setVehicle(vehicleData);
        
        // Only load photos for logged-in employees/vendors - not public customers
        if (currentUser) {
          const photoData = await publicApi.getVehiclePhotos(stockNumber);
          setPhotos(photoData.photos || []);
        } else {
          setPhotos([]); // No photos for public
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [stockNumber, currentUser]);

  // Load status data when employee role is selected
  useEffect(() => {
    if (selectedRole === 'employee' && vehicle) {
      publicApi.getVehicleStatus(stockNumber)
        .then(setStatusData)
        .catch(e => console.error('Failed to load status:', e));
    }
  }, [selectedRole, stockNumber, vehicle]);

  const handleSelectRole = (role) => {
    if (role === 'customer') {
      setShowDisclaimer(true);
    } else if (role === 'employee' && !currentUser) {
      // Require login for employee access
      navigate('/login', { state: { from: `/car/${stockNumber}` } });
    } else {
      setSelectedRole(role);
    }
  };
  
  const handleDisclaimerContinue = () => {
    setShowDisclaimer(false);
    setSelectedRole('customer');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-neutral-500">Loading vehicle details...</div>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-neutral-200 rounded-full flex items-center justify-center">
          <span className="text-2xl text-neutral-400">?</span>
        </div>
        <h1 className="text-2xl font-bold text-neutral-800 mb-2">Vehicle Not Found</h1>
        <p className="text-neutral-500 mb-6">This vehicle may have been sold or is no longer available.</p>
        <Link to="/cars" className="text-blue-600 hover:underline">Browse our inventory</Link>
      </div>
    );
  }

  // Check if vehicle is sold - show sold message (case-insensitive check)
  const vehicleStatus = (vehicle.status || '').toLowerCase();
  if (vehicleStatus === 'sold') {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
          <span className="text-2xl">🚗</span>
        </div>
        <h1 className="text-2xl font-bold text-neutral-800 mb-2">This Vehicle Has Sold</h1>
        <p className="text-neutral-500 mb-6">For more options, click View Inventory!</p>
        <Link 
          to="/cars" 
          className="inline-block px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          View Inventory
        </Link>
      </div>
    );
  }

  // Show role selector if no role selected yet
  if (!selectedRole && !showDisclaimer && !showSellForm && !showUpload && !showAIInspection) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <RoleSelector 
          vehicle={vehicle} 
          onSelectRole={handleSelectRole} 
          onSellCar={() => setShowSellForm(true)}
          onUpload={() => setShowUpload(true)}
          onBeginInspection={() => setShowAIInspection(true)}
        />
      </div>
    );
  }
  
  // Show AI-powered inspection flow
  if (showAIInspection) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <AIInspectionFlow 
          vehicle={vehicle} 
          stockNumber={stockNumber} 
          onBack={() => setShowAIInspection(false)} 
        />
      </div>
    );
  }
  
  // Show upload view (NO LOGIN REQUIRED)
  if (showUpload) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <UploadView 
          vehicle={vehicle} 
          stockNumber={stockNumber} 
          onBack={() => setShowUpload(false)} 
        />
      </div>
    );
  }
  
  // Show mark complete view (NO LOGIN REQUIRED - uses passcode)
  if (selectedRole === 'markComplete') {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <MarkCompleteView 
          vehicle={vehicle} 
          stockNumber={stockNumber} 
          onBack={() => setSelectedRole(null)} 
        />
      </div>
    );
  }
  
  // Show sell your car form
  if (showSellForm) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button 
          onClick={() => setShowSellForm(false)} 
          className="text-blue-600 hover:underline mb-6 flex items-center gap-1"
        >
          &larr; Back
        </button>
        <SellYourCarForm onClose={() => setShowSellForm(false)} />
      </div>
    );
  }
  
  // Show customer disclaimer screen
  if (showDisclaimer) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <CustomerDisclaimer vehicle={vehicle} onContinue={handleDisclaimerContinue} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <button 
          onClick={() => navigate('/cars')} 
          className="text-blue-600 hover:underline flex items-center gap-1"
        >
          &larr; Back to Inventory
        </button>
        
        {/* Change role button */}
        <button
          onClick={() => setSelectedRole(null)}
          className="text-xs text-neutral-500 hover:text-neutral-700 underline"
        >
          Change View
        </button>
      </div>

      {/* Role indicator */}
      <div className={`mb-4 px-3 py-1 rounded-full text-xs font-medium inline-block ${
        selectedRole === 'vendor' ? 'bg-amber-100 text-amber-700' : 
        selectedRole === 'employee' ? 'bg-neutral-200 text-neutral-700' :
        'bg-blue-100 text-blue-700'
      }`}>
        {selectedRole === 'vendor' ? 'Vendor View' : 
         selectedRole === 'employee' ? 'Employee View' : 
         'Customer View'}
      </div>

      {/* Render appropriate view based on selected role */}
      {selectedRole === 'vendor' && <VendorView vehicle={vehicle} photos={photos} stockNumber={stockNumber} />}
      {selectedRole === 'employee' && <EmployeeView vehicle={vehicle} photos={photos} statusData={statusData} />}
      {selectedRole === 'customer' && <CustomerView vehicle={vehicle} photos={photos} />}
    </div>
  );
};

// Main Public App
const PublicApp = () => {
  const location = useLocation();
  
  // Try to get auth context
  let currentUser = null;
  try {
    const auth = useAuth();
    currentUser = auth?.currentUser;
  } catch (e) {
    // Not in auth context
  }
  
  const isEmployee = currentUser && currentUser.role !== 'vendor';
  
  // Determine which page to show based on current path
  const isVehiclePage = location.pathname.startsWith('/car/');
  const isSellPage = location.pathname === '/cars/sell';
  const stockNumber = isVehiclePage ? location.pathname.split('/car/')[1] : null;
  
  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <Header isEmployee={isEmployee} />
      <main className="flex-1">
        {isVehiclePage ? (
          <VehiclePage key={stockNumber} />
        ) : isSellPage ? (
          <SellYourCarPage />
        ) : (
          <InventoryPage />
        )}
      </main>
      <Footer />
    </div>
  );
};

export default PublicApp;
